import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  leagueCustomerMembershipSettingsRoute,
  leagueCustomerRosterCustomFieldsRoute,
  leagueCustomerRosterMemberMediaRoute,
  leagueCustomerRosterMemberMedicalRecordRoute,
  leagueCustomerRosterMemberMedicalRecordsRoute,
  leagueCustomerRosterMemberRoute,
  leagueCustomerRosterMembersRoute,
} from "network/api-routes";
import countriesData from "constants/countries.json";
import PlayerPhotoUpload from "components/pages/Gestion/PlayerPhotoUpload";
import PlayerMedicalRecordUpload from "components/pages/Gestion/PlayerMedicalRecordUpload";
import GestionPlayerUserLinkDialog from "components/pages/Gestion/GestionPlayerUserLinkDialog";
import { toDateInputValue } from "components/pages/Gestion/player-form-helpers";
import { memberNumberSettingsFromApi, isManualMemberNumbers, showFamilyMemberAttach } from "./member-number-settings";

const emptyPlayerForm = {
  first_name: "",
  last_name: "",
  document_number: "",
  document_nationality: "",
  member_number: "",
  family_of: "",
  phone_number: "",
  email: "",
  medical_record_expires_at: "",
};

const isLinkedToUser = (player) => Boolean(player?.is_associated_to_user || player?.user_id || player?.linked_user);

const formFromPlayer = (player) => {
  if (!player) return emptyPlayerForm;
  return {
    first_name: player.first_name || "",
    last_name: player.last_name || "",
    document_number: player.document_number || "",
    document_nationality: isLinkedToUser(player)
      ? player.linked_user?.document_nationality || ""
      : player.document_nationality || "",
    member_number: player.member_number || "",
    family_of: "",
    phone_number: player.phone_number || "",
    email: player.email || "",
    medical_record_expires_at: toDateInputValue(player.medical_record_expires_at),
  };
};

/**
 * Create (player = null) or edit a roster member. `onChanged` runs after saves and media uploads.
 */
const PlayerFormDialog = ({ open, customerId, player, familyCandidates = [], onClose, onChanged }) => {
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [editingPlayer, setEditingPlayer] = useState(player);
  const [linkUserPlayer, setLinkUserPlayer] = useState(null);
  const [form, setForm] = useState(emptyPlayerForm);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingMedical, setUploadingMedical] = useState(false);
  const [removingMedical, setRemovingMedical] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [pendingMedicalFile, setPendingMedicalFile] = useState(null);
  const [numberSettings, setNumberSettings] = useState(memberNumberSettingsFromApi(null));
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [customValues, setCustomValues] = useState({});
  // "loading" | "ready" | "failed". Saved answers are only sent once they are "ready".
  const [customValuesStatus, setCustomValuesStatus] = useState("ready");
  const customValuesLoading = customValuesStatus === "loading";
  const customValuesFailed = customValuesStatus === "failed";
  const userRef = useRef(user);
  const isAssociatedToUser = isLinkedToUser(editingPlayer);
  const manualNumbers = isManualMemberNumbers(numberSettings);

  useEffect(() => {
    if (!open) return;
    setEditingPlayer(player);
    setForm(formFromPlayer(player));
    setPendingPhotoFile(null);
    setPendingMedicalFile(null);
  }, [open, player]);

  useEffect(() => {
    if (!open || !customerId) return;
    performRequest("get", leagueCustomerMembershipSettingsRoute(customerId), {}, true, user)
      .then((response) => setNumberSettings(memberNumberSettingsFromApi(response.data || {})))
      .catch(() => setNumberSettings(memberNumberSettingsFromApi(null)));
  }, [customerId, open, user]);

  useEffect(() => {
    if (!open || !customerId) return;
    performRequest("get", leagueCustomerRosterCustomFieldsRoute(customerId), {}, true, user)
      .then((response) => setCustomFieldDefs(Array.isArray(response.data) ? response.data : []))
      .catch(() => setCustomFieldDefs([]));
  }, [customerId, open, user]);

  // Read the latest user without re-running the reset when the context hands out a new object.
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (!open) return undefined;
    setCustomValues({});
    if (!customerId || !player?.id) {
      setCustomValuesStatus("ready");
      return undefined;
    }
    let active = true;
    setCustomValuesStatus("loading");
    performRequest("get", leagueCustomerRosterMemberRoute(customerId, player.id), {}, true, userRef.current)
      .then((response) => {
        if (!active) return;
        setCustomValues(response.data?.custom_field_values || {});
        setCustomValuesStatus("ready");
      })
      .catch(() => {
        if (active) setCustomValuesStatus("failed");
      });
    return () => {
      active = false;
    };
  }, [customerId, open, player?.id]);

  const handleError = useCallback(
    (error) => {
      enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
        variant: "error",
      });
    },
    [enqueueSnackbar, intl],
  );

  const uploadPlayerPhoto = (memberId, file) => {
    if (!customerId || !memberId || !file) return Promise.resolve(null);
    const body = new FormData();
    body.append("image", file);
    setUploadingPhoto(true);
    return performRequest("POST", leagueCustomerRosterMemberMediaRoute(customerId, memberId), body, true, user)
      .then((response) => response.data)
      .finally(() => setUploadingPhoto(false));
  };

  const uploadMedicalRecord = (memberId, file) => {
    if (!customerId || !memberId || !file) return Promise.resolve(null);
    const body = new FormData();
    body.append("medicalRecord", file);
    setUploadingMedical(true);
    return performRequest("POST", leagueCustomerRosterMemberMedicalRecordsRoute(customerId, memberId), body, true, user)
      .then((response) => response.data)
      .finally(() => setUploadingMedical(false));
  };

  const applyMediaUpdate = (updatedPlayer, messageId) => {
    setEditingPlayer((prev) => ({ ...prev, ...updatedPlayer }));
    enqueueSnackbar(intl.formatMessage({ id: messageId }), { variant: "success" });
    onChanged?.();
  };

  const handlePhotoSelect = (file) => {
    if (!file) return;
    if (editingPlayer?.id) {
      uploadPlayerPhoto(editingPlayer.id, file)
        .then((updated) => applyMediaUpdate(updated, "gestion.players.photoUploaded"))
        .catch(handleError);
      return;
    }
    setPendingPhotoFile(file);
  };

  const handleMedicalSelect = (file) => {
    if (!file) return;
    if (editingPlayer?.id) {
      uploadMedicalRecord(editingPlayer.id, file)
        .then((updated) => applyMediaUpdate(updated, "gestion.players.medicalUploaded"))
        .catch(handleError);
      return;
    }
    setPendingMedicalFile(file);
  };

  const handleMedicalRemove = () => {
    if (!customerId || !editingPlayer?.id) return;
    setRemovingMedical(true);
    performRequest("DELETE", leagueCustomerRosterMemberMedicalRecordRoute(customerId, editingPlayer.id), {}, true, user)
      .then((response) => applyMediaUpdate(response.data, "gestion.players.medicalRemoved"))
      .catch(handleError)
      .finally(() => setRemovingMedical(false));
  };

  const setCustomValue = (def, value) => setCustomValues((prev) => ({ ...prev, [String(def.id)]: value }));

  const buildSavePayload = () => ({
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim() || null,
    document_number: form.document_number.trim() || null,
    ...(!isAssociatedToUser ? { document_nationality: form.document_nationality || null } : {}),
    member_number: form.member_number.trim() || null,
    ...(form.family_of ? { member_number_family_of: Number(form.family_of) } : {}),
    phone_number: form.phone_number.trim() || null,
    email: form.email.trim() || null,
    medical_record_expires_at: form.medical_record_expires_at || null,
    // Omitted while saved answers are loading or after a failed GET, so blanks never overwrite them.
    ...(customFieldDefs.length > 0 && customValuesStatus === "ready"
      ? {
          custom_fields: Object.fromEntries(
            customFieldDefs.map((def) => [String(def.id), customValues[String(def.id)] ?? (def.type === "boolean" ? false : "")]),
          ),
        }
      : {}),
  });

  // Only enforced when the answers are actually sent; after a failed load they are not.
  const missingCustomRequired =
    customValuesStatus === "ready" &&
    customFieldDefs.some((def) => def.required && def.type !== "boolean" && !String(customValues[String(def.id)] ?? "").trim());

  const missingRequired =
    !form.first_name.trim() ||
    (manualNumbers && !form.member_number.trim() && !form.family_of) ||
    missingCustomRequired;

  const handleSave = () => {
    if (!customerId || missingRequired) return;
    setSaving(true);
    const method = editingPlayer ? "PATCH" : "POST";
    const url = editingPlayer
      ? leagueCustomerRosterMemberRoute(customerId, editingPlayer.id)
      : leagueCustomerRosterMembersRoute(customerId);

    performRequest(method, url, buildSavePayload(), true, user)
      .then((response) => {
        const savedPlayer = response.data;
        const uploads = [];
        if (pendingPhotoFile && savedPlayer?.id) uploads.push(uploadPlayerPhoto(savedPlayer.id, pendingPhotoFile));
        if (pendingMedicalFile && savedPlayer?.id) uploads.push(uploadMedicalRecord(savedPlayer.id, pendingMedicalFile));
        return Promise.all(uploads);
      })
      .then(() => {
        enqueueSnackbar(
          intl.formatMessage({ id: editingPlayer ? "gestion.players.updated" : "gestion.players.created" }),
          { variant: "success" },
        );
        onClose();
        onChanged?.();
      })
      .catch(handleError)
      .finally(() => setSaving(false));
  };

  const familyOptions = familyCandidates.filter(
    (candidate) => candidate.member_number && candidate.id !== editingPlayer?.id,
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          {intl.formatMessage({ id: editingPlayer ? "gestion.players.edit" : "gestion.players.create" })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ sm: "flex-start" }}>
              <PlayerPhotoUpload
                key={editingPlayer?.id || "new"}
                player={editingPlayer}
                onFileSelect={handlePhotoSelect}
                uploading={uploadingPhoto}
                saveFirst={!editingPlayer?.id}
              />
              <Stack spacing={2} sx={{ flex: 1, width: "100%" }}>
                <TextField
                  label={intl.formatMessage({ id: "gestion.players.firstName" })}
                  value={form.first_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, first_name: event.target.value }))}
                  required
                  fullWidth
                />
                <TextField
                  label={intl.formatMessage({ id: "gestion.players.lastName" })}
                  value={form.last_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, last_name: event.target.value }))}
                  fullWidth
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Autocomplete
                    options={countriesData}
                    autoHighlight
                    disabled={isAssociatedToUser}
                    getOptionLabel={(option) => option.name}
                    value={countriesData.find((country) => country.code === form.document_nationality) || null}
                    onChange={(_event, country) =>
                      setForm((prev) => ({
                        ...prev,
                        document_nationality: country?.code || "",
                      }))
                    }
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                        <Box key={key} component="li" {...optionProps}>
                          <Box component="span" sx={{ mr: 1 }}>
                            {option.flag}
                          </Box>
                          {option.name}
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={intl.formatMessage({ id: "gestion.players.documentNationality" })}
                        helperText={
                          isAssociatedToUser
                            ? intl.formatMessage({ id: "gestion.players.documentNationalityLinkedHint" })
                            : undefined
                        }
                      />
                    )}
                    sx={{ minWidth: { sm: 240 }, flex: { sm: "0 0 42%" } }}
                  />
                  <TextField
                    label={intl.formatMessage({ id: "gestion.players.document" })}
                    value={form.document_number}
                    onChange={(event) => setForm((prev) => ({ ...prev, document_number: event.target.value }))}
                    fullWidth
                  />
                </Stack>
                <TextField
                  label={intl.formatMessage({ id: "gestion.players.memberNumber" })}
                  value={form.member_number}
                  onChange={(event) => setForm((prev) => ({ ...prev, member_number: event.target.value, family_of: "" }))}
                  required={manualNumbers && !form.family_of}
                  helperText={intl.formatMessage({
                    id: manualNumbers ? "gestion.players.memberNumberRequired" : "gestion.players.memberNumberAutomaticHint",
                  })}
                  fullWidth
                />
                {showFamilyMemberAttach(numberSettings) && familyOptions.length > 0 ? (
                  <Autocomplete
                    options={familyOptions}
                    getOptionLabel={(candidate) =>
                      `${[candidate.first_name, candidate.last_name].filter(Boolean).join(" ")} (${candidate.member_number})`
                    }
                    value={familyOptions.find((candidate) => String(candidate.id) === String(form.family_of)) || null}
                    onChange={(_event, candidate) =>
                      setForm((prev) => ({
                        ...prev,
                        family_of: candidate?.id ? String(candidate.id) : "",
                        member_number: "",
                      }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={intl.formatMessage({ id: "gestion.players.memberNumberFamily" })} />
                    )}
                  />
                ) : null}
                <TextField
                  label={intl.formatMessage({ id: "gestion.players.phone" })}
                  value={form.phone_number}
                  onChange={(event) => setForm((prev) => ({ ...prev, phone_number: event.target.value }))}
                  fullWidth
                />
                <TextField
                  label={intl.formatMessage({ id: "gestion.players.email" })}
                  value={form.email}
                  onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  fullWidth
                />
                {customFieldDefs.length > 0 ? (
                  <>
                    <Divider />
                    <RevaText variant="subtitle2">{intl.formatMessage({ id: "gestion.players.customFields" })}</RevaText>
                    {customValuesFailed ? (
                      <Alert severity="warning">
                        {intl.formatMessage({ id: "gestion.players.customFieldsLoadFailed" })}
                      </Alert>
                    ) : null}
                    {customFieldDefs.map((def) => {
                      const key = String(def.id);
                      const value = customValues[key];
                      if (def.type === "boolean") {
                        return (
                          <FormControlLabel
                            key={key}
                            control={
                              <Switch
                                checked={value === true || value === "true"}
                                onChange={(event) => setCustomValue(def, event.target.checked)}
                              />
                            }
                            label={def.label}
                          />
                        );
                      }
                      if (def.type === "single_select") {
                        return (
                          <TextField
                            key={key}
                            select
                            label={def.label}
                            value={value ?? ""}
                            onChange={(event) => setCustomValue(def, event.target.value)}
                            required={def.required}
                            helperText={def.help_text || undefined}
                            fullWidth
                          >
                            {def.required ? null : <MenuItem value="" />}
                            {(def.options || []).map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        );
                      }
                      const multiline = def.type === "long_text";
                      return (
                        <TextField
                          key={key}
                          label={def.label}
                          value={value ?? ""}
                          onChange={(event) => setCustomValue(def, event.target.value)}
                          placeholder={def.placeholder || undefined}
                          helperText={def.help_text || undefined}
                          required={def.required}
                          multiline={multiline}
                          minRows={multiline ? 3 : undefined}
                          inputProps={{ maxLength: multiline ? 2000 : 150 }}
                          fullWidth
                        />
                      );
                    })}
                  </>
                ) : null}
              </Stack>
            </Stack>

            <Divider />

            <PlayerMedicalRecordUpload
              player={editingPlayer}
              expiresAt={form.medical_record_expires_at}
              onExpiresAtChange={(value) => setForm((prev) => ({ ...prev, medical_record_expires_at: value }))}
              onFileSelect={handleMedicalSelect}
              onRemove={handleMedicalRemove}
              uploading={uploadingMedical}
              removing={removingMedical}
              saveFirst={!editingPlayer?.id}
            />

            {editingPlayer ? (
              <>
                <Divider />
                <Stack spacing={1}>
                  <RevaText variant="subtitle2">{intl.formatMessage({ id: "gestion.players.status" })}</RevaText>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <Chip
                      size="small"
                      label={intl.formatMessage({
                        id: isAssociatedToUser ? "gestion.players.associatedToUser" : "gestion.players.notAssociatedToUser",
                      })}
                      color={isAssociatedToUser ? "success" : "default"}
                      variant="outlined"
                    />
                    <Button size="small" variant="outlined" onClick={() => setLinkUserPlayer(editingPlayer)}>
                      {intl.formatMessage({
                        id: isAssociatedToUser ? "gestion.players.manageUserLink" : "gestion.players.linkUser",
                      })}
                    </Button>
                  </Stack>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {intl.formatMessage({ id: "gestion.players.contextualFieldsHint" })}
                  </Alert>
                </Stack>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={onClose}>
            {intl.formatMessage({ id: "Cancelar" })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || missingRequired || customValuesLoading}
          >
            {intl.formatMessage({ id: saving ? "Guardando..." : "Guardar" })}
          </Button>
        </DialogActions>
      </Dialog>

      <GestionPlayerUserLinkDialog
        open={Boolean(linkUserPlayer)}
        onClose={() => setLinkUserPlayer(null)}
        customerId={customerId}
        player={linkUserPlayer}
        onUpdated={(updated) => {
          if (updated && editingPlayer?.id === updated.id) {
            setEditingPlayer((prev) => ({ ...prev, ...updated }));
          }
          onChanged?.();
        }}
      />
    </>
  );
};

export default PlayerFormDialog;
